import { useCallback, useEffect, useMemo, useState } from 'react';
import PlayerAvatar from './PlayerAvatar.jsx';
import { helpers } from '../services/rootingScores.js';
import {
  loadDashboardPrefs,
  saveDashboardPrefs,
} from '../services/leagueStorage.js';
import {
  DEFAULT_SLEEPER_USERNAME,
  SAMPLE_SLEEPER_LEAGUES,
  SAMPLE_LEAGUE_IDS,
} from '../config/leagueDefaults.js';
import {
  fetchNflState,
  fetchWeekDashboard,
  discoverUserLeagues,
  buildRosterMoves,
} from '../services/weekDashboard.js';
import { slotLabel } from '../services/matchupBoard.js';

/** Two-line slot labels in the narrow fold table column (readable, no bleed into avatars). */
const FOLD_STACKED_SLOT_LINES = {
  SUPER_FLEX: ['SUPER', 'FLEX'],
  REC_FLEX: ['REC', 'FLEX'],
};

const NFL_WEEKS = Array.from({ length: 18 }, (_, i) => i + 1);
const LINEUP_FIXES_MAX = 3;
const FOLD_MOVE_TYPES = new Set(['injury', 'start', 'flex']);
const SWING_THRESHOLD = 0.1;
const SWING_VISIBLE_BARS = 8;

const DECIDE_PANELS = [
  { id: 'start-sit', label: 'Start/sit' },
  { id: 'waivers', label: 'Waivers' },
  { id: 'trades', label: 'Trades' },
];

const formatSigned = (value) => {
  const numeric = Number(value || 0);
  const base = helpers.formatNumber(numeric);
  return numeric > 0 ? `+${base}` : base;
};

const formatPoints = (value, source) => {
  const base = helpers.formatNumber(value);
  if (source === 'projection') return `~${base}`;
  return base;
};

const playStateLabel = (starter) => {
  if (starter.hasPlayed) return 'Played';
  if (starter.isLocked) return 'Locked';
  return null;
};

const playStateClass = (starter) => starter.playState || 'open';

const isInjuryHighlight = (starter) => starter?.injuryAlert;

const pickFoldLeague = (leagues, rosterMoves) => {
  if (!leagues?.length) return null;
  const injuryMoves = (rosterMoves || []).filter((m) => m.type === 'injury');
  if (injuryMoves.length) {
    const top = injuryMoves[0];
    const match = leagues.find((l) => l.leagueName === top.leagueName);
    if (match) return match;
  }
  const foldTypes = new Set(['injury', 'start', 'flex']);
  for (const move of rosterMoves || []) {
    if (!foldTypes.has(move.type)) continue;
    const match = leagues.find((l) => l.leagueName === move.leagueName);
    if (match) return match;
  }
  const withMyInjury = leagues.find((l) =>
    l.myTeam?.starters?.some((s) => s.injuryAlert)
  );
  return withMyInjury || leagues[0];
};

const LineupFixRow = ({ move }) => (
  <li className="ff-lineup-fix">
    <div className="ff-lineup-fix__body">
      {move.outPlayer && move.inPlayer && (
        <span className="ff-lineup-fix__swap">
          <PlayerAvatar
            size="sm"
            className="ff-lineup-fix__out"
            name={move.outPlayer.name}
            team={move.outPlayer.team}
            position={move.outPlayer.position}
            sleeperId={move.outPlayer.id}
          />
          <span className="ff-lineup-fix__arrow" aria-hidden="true">→</span>
          <PlayerAvatar
            size="sm"
            name={move.inPlayer.name}
            team={move.inPlayer.team}
            position={move.inPlayer.position}
            sleeperId={move.inPlayer.id}
          />
        </span>
      )}
      <span className="ff-lineup-fix__text">
        <p className="ff-lineup-fix__line">{move.line}</p>
        <p className="ff-lineup-fix__why">{move.why}</p>
      </span>
    </div>
  </li>
);

const LineupFixesFold = ({ league, moves }) => {
  if (!moves.length) return null;
  return (
    <section className="ff-lineup-fixes" aria-label="Lineup fixes">
      <header className="ff-lineup-fixes__header">
        <h2 className="ff-lineup-fixes__title">Sit → start</h2>
        <span className="ff-lineup-fixes__league">{league.leagueName}</span>
      </header>
      <ol className="ff-lineup-fixes__list">
        {moves.map((move) => (
          <LineupFixRow key={move.id} move={move} />
        ))}
      </ol>
    </section>
  );
};

const FoldPlayerCell = ({ starter, side }) => {
  if (!starter) {
    return <span className="ff-fold-cell ff-fold-cell--empty">—</span>;
  }
  const stateLabel = playStateLabel(starter);
  const stateClass = playStateClass(starter);
  return (
    <span className={`ff-fold-cell ff-fold-cell--${side}`}>
      <span className="ff-fold-cell__face">
        <PlayerAvatar
          size="xs"
          name={starter.name}
          team={starter.team}
          position={starter.position}
          sleeperId={starter.id}
        />
        <span className="ff-fold-cell__primary">
          <span className="ff-fold-cell__name">{starter.name}</span>
          <span className="ff-fold-cell__pts">
            {formatPoints(starter.points, starter.pointSource)}
          </span>
        </span>
      </span>
      {(starter.injuryStatus || stateLabel) && (
        <span className="ff-fold-cell__badges">
          {starter.injuryStatus && (
            <span className="ff-fold-cell__injury">{starter.injuryStatus}</span>
          )}
          {stateLabel && (
            <span className={`ff-fold-cell__state ff-fold-cell__state--${stateClass}`}>
              {stateLabel}
            </span>
          )}
        </span>
      )}
    </span>
  );
};

const FoldSlotLabel = ({ slot }) => {
  const stacked = FOLD_STACKED_SLOT_LINES[slot];
  if (stacked) {
    return (
      <span
        className="ff-fold-slot-row__slot ff-fold-slot-row__slot--stacked"
        aria-label={stacked.join(' ')}
      >
        {stacked.map((line) => (
          <span key={line} className="ff-fold-slot-row__slot-line">{line}</span>
        ))}
      </span>
    );
  }
  return <span className="ff-fold-slot-row__slot">{slotLabel(slot)}</span>;
};

const FoldSlotRow = ({ slot, mine, opp }) => {
  const injuryRow = isInjuryHighlight(mine);
  return (
    <li
      className={`ff-fold-slot-row ${injuryRow ? 'ff-fold-slot-row--injury' : ''}`}
    >
      <FoldSlotLabel slot={slot} />
      <FoldPlayerCell starter={mine} side="mine" />
      <FoldPlayerCell starter={opp} side="opp" />
    </li>
  );
};

const FoldMatchupCard = ({ league, stickyScore = false }) => {
  if (!league) return null;
  const margin = league.myTeam.points - league.opponent.points;
  const slots = league.rosterPositions?.filter((s) =>
    ['QB', 'RB', 'WR', 'TE', 'FLEX', 'SUPER_FLEX', 'REC_FLEX', 'K', 'DEF'].includes(s)
  ) || [];

  const rows = league.myTeam.starters.map((starter, index) => ({
    slot: slots[index] || starter.position || '—',
    mine: starter,
    opp: league.opponent.starters[index],
  }));

  return (
    <article
      className={`ff-fold-matchup ${stickyScore ? 'ff-fold-matchup--sticky-score' : ''}`}
      aria-label={`Matchup: ${league.leagueName}`}
    >
      <header className="ff-fold-matchup__header">
        <div className="ff-fold-matchup__meta">
          <h2 className="ff-fold-matchup__league">{league.leagueName}</h2>
          <p className="ff-fold-matchup__teams">
            {league.myTeam.name} vs {league.opponent.name}
          </p>
        </div>
        <div className="ff-fold-matchup__score">
          <span className="ff-fold-matchup__mine">
            {helpers.formatNumber(league.myTeam.points)}
          </span>
          <span className="ff-fold-matchup__sep">–</span>
          <span className="ff-fold-matchup__opp">
            {helpers.formatNumber(league.opponent.points)}
          </span>
          {margin !== 0 && (
            <span
              className={`ff-fold-matchup__margin ${
                margin > 0 ? 'ff-fold-matchup__margin--up' : 'ff-fold-matchup__margin--down'
              }`}
            >
              {formatSigned(margin)}
            </span>
          )}
        </div>
      </header>

      <div className="ff-fold-table" role="table" aria-label="Matchup starters">
        <div className="ff-fold-table__head" role="row">
          <span className="ff-fold-table__col-slot" role="columnheader" />
          <span className="ff-fold-table__col-you" role="columnheader">You</span>
          <span className="ff-fold-table__col-opp" role="columnheader">Opp</span>
        </div>
        <ul className="ff-fold-table__body" role="rowgroup">
          {rows.map(({ slot, mine, opp }) => (
            <FoldSlotRow
              key={mine.id}
              slot={slot}
              mine={mine}
              opp={opp}
            />
          ))}
        </ul>
      </div>
    </article>
  );
};

const RootingExposureChart = ({ totals }) => {
  if (!totals) return null;

  const support = Math.max(0, totals.support || 0);
  const against = Math.max(0, totals.against || 0);
  const maxBar = Math.max(support, against, 1);
  const supportPct = Math.round((support / maxBar) * 100);
  const againstPct = Math.round((against / maxBar) * 100);
  const net = totals.net || 0;
  const netPositive = net >= 0;

  return (
    <div className="rooting-chart" aria-label="Cross-league rooting exposure">
      <div className="rooting-chart__row">
        <span className="rooting-chart__label">FOR</span>
        <div className="rooting-chart__track">
          <span
            className="rooting-chart__bar rooting-chart__bar--for"
            style={{ width: `${supportPct}%` }}
          />
        </div>
        <span className="rooting-chart__value rooting-chart__value--positive">
          {formatSigned(support)}
        </span>
      </div>
      <div className="rooting-chart__row">
        <span className="rooting-chart__label">VS</span>
        <div className="rooting-chart__track">
          <span
            className="rooting-chart__bar rooting-chart__bar--against"
            style={{ width: `${againstPct}%` }}
          />
        </div>
        <span className="rooting-chart__value rooting-chart__value--negative">
          {formatSigned(-against)}
        </span>
      </div>
      <div className="rooting-chart__net">
        <span className="rooting-chart__net-label">NET</span>
        <span
          className={`rooting-chart__net-value ${
            netPositive ? 'rooting-chart__net-value--positive' : 'rooting-chart__net-value--negative'
          }`}
        >
          {formatSigned(net)}
        </span>
      </div>
    </div>
  );
};

const NetSwingBarChart = ({ players, limit }) => {
  if (!players.length) return null;

  const maxSwing = Math.max(...players.map((p) => Math.abs(p.net)), 1);
  const chartPlayers = players.slice(0, limit ?? players.length);

  return (
    <div className="ff-swing-chart" aria-label="Net swing by player">
      {chartPlayers.map((player) => {
        const widthPct = Math.round((Math.abs(player.net) / maxSwing) * 100);
        const positive = player.net >= 0;
        return (
          <div key={player.id} className="ff-swing-chart__row">
            <span className="ff-swing-chart__name" title={player.name}>
              <PlayerAvatar
                size="xs"
                name={player.name}
                team={player.team}
                position={player.position}
                sleeperId={player.id}
              />
              <span className="ff-avatar-name__label">{player.name}</span>
            </span>
            <div className="ff-swing-chart__track">
              <span
                className={`ff-swing-chart__bar ${
                  positive ? 'ff-swing-chart__bar--for' : 'ff-swing-chart__bar--against'
                }`}
                style={{ width: `${widthPct}%` }}
              />
            </div>
            <span
              className={`ff-swing-chart__value ${
                positive ? 'ff-swing-chart__value--positive' : 'ff-swing-chart__value--negative'
              }`}
            >
              {formatSigned(player.net)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const ScopeChips = ({ leagues, activeScope, onSelect }) => (
  <div className="ff-scope-chips" role="tablist" aria-label="League scope">
    <button
      type="button"
      role="tab"
      className={`ff-scope-chip ${activeScope === 'all' ? 'is-active' : ''}`}
      aria-selected={activeScope === 'all'}
      onClick={() => onSelect('all')}
    >
      All
    </button>
    {leagues.map((league) => (
      <button
        key={league.leagueId}
        type="button"
        role="tab"
        className={`ff-scope-chip ${activeScope === league.leagueId ? 'is-active' : ''}`}
        aria-selected={activeScope === league.leagueId}
        onClick={() => onSelect(league.leagueId)}
      >
        {league.leagueName}
      </button>
    ))}
  </div>
);

const DecideSubChips = ({ activePanel, onSelect }) => (
  <div className="ff-scope-chips ff-scope-chips--decide" role="tablist" aria-label="Decide">
    {DECIDE_PANELS.map((panel) => (
      <button
        key={panel.id}
        type="button"
        role="tab"
        className={`ff-scope-chip ${activePanel === panel.id ? 'is-active' : ''}`}
        aria-selected={activePanel === panel.id}
        onClick={() => onSelect(panel.id)}
      >
        {panel.label}
      </button>
    ))}
  </div>
);

const WaiversPanel = () => (
  <section className="ff-decide-panel" aria-label="Waivers">
    <p className="ff-decide-panel__lede">
      Waivers run Tuesday morning. Your adds and drops will show here once the window opens.
    </p>
    <p className="dashboard-results__empty">No pending waiver moves this week.</p>
  </section>
);

const TradesPanel = ({ onGenerate, generated, loading }) => (
  <section className="ff-decide-panel" aria-label="Trades">
    {!generated ? (
      <>
        <p className="ff-decide-panel__lede">
          Trade ideas are generated on demand — nothing stays pinned when you are not looking.
        </p>
        <button
          type="button"
          className="dashboard-form__submit ff-decide-panel__action"
          onClick={onGenerate}
          disabled={loading}
        >
          {loading ? 'Thinking…' : 'Generate trade ideas'}
        </button>
      </>
    ) : (
      <p className="dashboard-results__empty">
        No clear two-for-one upgrades this week. Check back after injuries settle.
      </p>
    )}
  </section>
);

const WeekStepper = ({ weekNum, displayWeek, onPrev, onNext, loading }) => {
  const atMin = weekNum <= 1;
  const atMax = weekNum >= NFL_WEEKS.length;
  return (
    <div className="dashboard__week-nav">
      <button
        type="button"
        className="dashboard__week-step"
        onClick={onPrev}
        disabled={loading || atMin}
        aria-label="Previous week"
      >
        ◀
      </button>
      <h1 className="dashboard__week-title">Week {displayWeek}</h1>
      <button
        type="button"
        className="dashboard__week-step"
        onClick={onNext}
        disabled={loading || atMax}
        aria-label="Next week"
      >
        ▶
      </button>
    </div>
  );
};

const Dashboard = ({ mode = 'matchups' }) => {
  const isMatchups = mode === 'matchups';
  const isDecide = mode === 'decide';
  const savedPrefs = loadDashboardPrefs();
  const [username, setUsername] = useState(() => savedPrefs.username || DEFAULT_SLEEPER_USERNAME);
  const [week, setWeek] = useState('');
  const [useSampleLeagues, setUseSampleLeagues] = useState(
    () => savedPrefs.useSampleLeagues ?? false
  );
  const [selectedLeagueIds, setSelectedLeagueIds] = useState(
    () => savedPrefs.leagueIds || []
  );
  const [discoveredLeagues, setDiscoveredLeagues] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [matchupScope, setMatchupScope] = useState('all');
  const [decidePanel, setDecidePanel] = useState('start-sit');
  const [showMoreSwings, setShowMoreSwings] = useState(false);
  const [tradesGenerated, setTradesGenerated] = useState(false);
  const [tradesLoading, setTradesLoading] = useState(false);

  const loadWeek = useCallback(async () => {
    const state = await fetchNflState();
    const currentWeek = Number(state.week || 1);
    setWeek((prev) => prev || String(currentWeek));
    return currentWeek;
  }, []);

  useEffect(() => {
    loadWeek().catch(() => setWeek('1'));
  }, [loadWeek]);

  const discoverLeagues = useCallback(async () => {
    if (!username.trim()) return;
    try {
      const state = await fetchNflState();
      const season = Number(state.league_season || state.season);
      const { leagues } = await discoverUserLeagues(username.trim(), season);
      setDiscoveredLeagues(leagues);
      if (!useSampleLeagues && leagues.length && !selectedLeagueIds.length) {
        setSelectedLeagueIds(leagues.map((l) => l.league_id));
      }
    } catch {
      setDiscoveredLeagues([]);
    }
  }, [username, useSampleLeagues, selectedLeagueIds.length]);

  useEffect(() => {
    discoverLeagues();
  }, [discoverLeagues]);

  const leagueIdsToLoad = useMemo(() => {
    if (useSampleLeagues) return SAMPLE_LEAGUE_IDS;
    if (selectedLeagueIds.length) return selectedLeagueIds;
    if (discoveredLeagues.length) return discoveredLeagues.map((l) => l.league_id);
    return SAMPLE_LEAGUE_IDS;
  }, [useSampleLeagues, selectedLeagueIds, discoveredLeagues]);

  const loadDashboard = useCallback(async () => {
    const weekNum = Number(week);
    if (!username.trim() || !weekNum) return;

    setLoading(true);
    setError('');

    try {
      const result = await fetchWeekDashboard({
        username: username.trim(),
        week: weekNum,
        leagueIds: leagueIdsToLoad,
      });
      setData(result);
      setLastUpdated(new Date());
      saveDashboardPrefs({
        username: username.trim(),
        leagueIds: leagueIdsToLoad,
        useSampleLeagues,
      });
    } catch (err) {
      setData(null);
      setError(err.message || 'Could not load week dashboard.');
    } finally {
      setLoading(false);
    }
  }, [username, week, leagueIdsToLoad, useSampleLeagues]);

  useEffect(() => {
    if (week) {
      loadDashboard();
    }
  }, [week, leagueIdsToLoad.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = (event) => {
    event?.preventDefault();
    loadDashboard();
  };

  const toggleLeague = (leagueId) => {
    setUseSampleLeagues(false);
    setSelectedLeagueIds((prev) =>
      prev.includes(leagueId) ? prev.filter((id) => id !== leagueId) : [...prev, leagueId]
    );
  };

  const weekNum = Number(week) || Number(data?.week) || 1;
  const displayWeek = data?.week || week || '—';

  const stepWeek = (delta) => {
    const next = Math.min(NFL_WEEKS.length, Math.max(1, weekNum + delta));
    setWeek(String(next));
  };

  const foldLeague = useMemo(
    () => pickFoldLeague(data?.leagues, data?.rosterMoves),
    [data?.leagues, data?.rosterMoves]
  );

  const foldLineupFixes = useMemo(() => {
    if (!foldLeague) return [];
    return buildRosterMoves([foldLeague], LINEUP_FIXES_MAX).filter((m) =>
      FOLD_MOVE_TYPES.has(m.type)
    );
  }, [foldLeague]);

  const scopedLeague = useMemo(() => {
    if (!data?.leagues?.length) return null;
    if (matchupScope === 'all') return null;
    return data.leagues.find((l) => l.leagueId === matchupScope) || data.leagues[0];
  }, [data?.leagues, matchupScope]);

  const totals = data?.rooting?.totals;
  const swingPlayers = useMemo(() => {
    const all = data?.rooting?.players ?? [];
    return all
      .filter((p) => Math.abs(p.net) >= SWING_THRESHOLD)
      .sort((a, b) => Math.abs(b.net) - Math.abs(a.net));
  }, [data?.rooting?.players]);
  const visibleSwingCount = showMoreSwings
    ? swingPlayers.length
    : Math.min(SWING_VISIBLE_BARS, swingPlayers.length);
  const hiddenSwingCount = Math.max(0, swingPlayers.length - SWING_VISIBLE_BARS);
  const leagueCount = data?.leagues?.length ?? 0;

  useEffect(() => {
    setShowMoreSwings(false);
    setTradesGenerated(false);
  }, [swingPlayers.length, data?.week, mode]);

  useEffect(() => {
    if (matchupScope !== 'all' && data?.leagues?.length) {
      const exists = data.leagues.some((l) => l.leagueId === matchupScope);
      if (!exists) setMatchupScope('all');
    }
  }, [data?.leagues, matchupScope]);

  const handleGenerateTrades = () => {
    setTradesLoading(true);
    window.setTimeout(() => {
      setTradesGenerated(true);
      setTradesLoading(false);
    }, 400);
  };

  return (
    <section
      className={`dashboard dashboard--week dashboard--fold ${
        isMatchups ? 'dashboard--matchups' : 'dashboard--decide'
      }`}
    >
      <div className="dashboard__top-bar dashboard__top-bar--compact">
        <WeekStepper
          weekNum={weekNum}
          displayWeek={displayWeek}
          onPrev={() => stepWeek(-1)}
          onNext={() => stepWeek(1)}
          loading={loading && !data}
        />
        {data && (
          <span className="dashboard__week-meta">
            {leagueCount} league{leagueCount !== 1 ? 's' : ''}
          </span>
        )}
        <button
          type="button"
          className="dashboard__settings-toggle"
          onClick={() => setShowSettings((open) => !open)}
          aria-expanded={showSettings}
          aria-controls="dashboard-settings"
          aria-label={showSettings ? 'Close setup' : 'Open setup'}
        >
          <span className="dashboard__settings-text">
            {showSettings ? 'Close' : 'Setup'}
          </span>
          <span className="dashboard__settings-icon" aria-hidden="true">⚙</span>
        </button>
      </div>

      <section className="dashboard__layout dashboard__layout--week">
        {showSettings && (
          <section
            id="dashboard-settings"
            className="dashboard__panel dashboard__panel--form"
          >
            <form className="dashboard-form" onSubmit={handleRefresh}>
              <div className="dashboard-form__field">
                <label htmlFor="sleeperUsername">Sleeper username</label>
                <input
                  id="sleeperUsername"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="doblerone"
                />
              </div>

              <div className="dashboard-form__field">
                <label htmlFor="week">Week</label>
                <div className="dashboard-form__field-row">
                  <select id="week" value={week} onChange={(e) => setWeek(e.target.value)}>
                    {NFL_WEEKS.map((w) => (
                      <option key={w} value={w}>Week {w}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="dashboard-form__week-reset"
                    onClick={async () => {
                      const w = await loadWeek();
                      setWeek(String(w));
                    }}
                  >
                    Current
                  </button>
                </div>
              </div>

              <fieldset className="dashboard-form__fieldset">
                <legend className="dashboard-form__legend">Leagues</legend>
                <label className="week-league-toggle">
                  <input
                    type="checkbox"
                    checked={useSampleLeagues}
                    onChange={(e) => {
                      setUseSampleLeagues(e.target.checked);
                      if (e.target.checked) {
                        setSelectedLeagueIds(SAMPLE_LEAGUE_IDS);
                      }
                    }}
                  />
                  Use sample leagues (doblerone&apos;s three 2026 leagues)
                </label>
                {!useSampleLeagues && discoveredLeagues.length > 0 && (
                  <ul className="week-league-pick-list">
                    {discoveredLeagues.map((l) => (
                      <li key={l.league_id}>
                        <label>
                          <input
                            type="checkbox"
                            checked={selectedLeagueIds.includes(l.league_id)}
                            onChange={() => toggleLeague(l.league_id)}
                          />
                          {l.name}
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
                {useSampleLeagues && (
                  <ul className="week-sample-list">
                    {SAMPLE_SLEEPER_LEAGUES.map((l) => (
                      <li key={l.leagueId}>
                        <strong>{l.name}</strong> — {l.teamName}
                        {l.note && <span className="week-sample-list__note"> ({l.note})</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </fieldset>

              {error && (
                <p className="dashboard-form__error" role="alert">{error}</p>
              )}

              <button className="dashboard-form__submit" type="submit" disabled={loading}>
                {loading ? 'Loading…' : 'Refresh'}
              </button>
            </form>
          </section>
        )}

        <div className="dashboard__main">
          {error && !showSettings && (
            <p className="dashboard-form__error dashboard__inline-error" role="alert">{error}</p>
          )}

          {isMatchups && data?.leagues?.length > 0 && (
            <ScopeChips
              leagues={data.leagues}
              activeScope={matchupScope}
              onSelect={setMatchupScope}
            />
          )}

          {isDecide && (
            <DecideSubChips activePanel={decidePanel} onSelect={setDecidePanel} />
          )}

          {loading && !data ? (
            <p className="roster-moves__loading" role="status" aria-live="polite">
              Loading your leagues…
            </p>
          ) : (
            <>
              {isMatchups && matchupScope === 'all' && (
                <section
                  className="ff-section ff-section--rooting dashboard__panel dashboard__panel--results"
                  aria-label="All leagues swing"
                >
                  <div className="dashboard-results">
                    <header className="dashboard-results__header dashboard-results__header--compact">
                      {totals && <RootingExposureChart totals={totals} />}
                    </header>
                    <div className="dashboard-results__body">
                      {swingPlayers.length > 0 ? (
                        <div className="ff-swing-board">
                          <NetSwingBarChart
                            players={swingPlayers}
                            limit={visibleSwingCount}
                          />
                          {hiddenSwingCount > 0 && (
                            <button
                              type="button"
                              className="ff-swing-board__toggle"
                              onClick={() => setShowMoreSwings((open) => !open)}
                              aria-expanded={showMoreSwings}
                            >
                              {showMoreSwings
                                ? 'Show fewer'
                                : `Show more (${hiddenSwingCount} more)`}
                            </button>
                          )}
                        </div>
                      ) : (
                        <p className="dashboard-results__empty">
                          No meaningful swing yet — open Setup if leagues did not load.
                        </p>
                      )}
                    </div>
                    {lastUpdated && (
                      <p className="dashboard-results__timestamp">
                        Updated {lastUpdated.toLocaleTimeString()}
                      </p>
                    )}
                  </div>
                </section>
              )}

              {isMatchups && matchupScope !== 'all' && scopedLeague && (
                <section className="ff-section ff-fold" aria-label="League matchup">
                  <FoldMatchupCard league={scopedLeague} stickyScore />
                </section>
              )}

              {isDecide && decidePanel === 'start-sit' && (
                <section className="ff-section ff-fold" aria-label="Start and sit">
                  {foldLineupFixes.length > 0 && foldLeague && (
                    <LineupFixesFold league={foldLeague} moves={foldLineupFixes} />
                  )}
                  {foldLeague && <FoldMatchupCard league={foldLeague} />}
                  {!foldLeague && (
                    <p className="dashboard-results__empty">No lineup loaded — check Setup.</p>
                  )}
                </section>
              )}

              {isDecide && decidePanel === 'waivers' && <WaiversPanel />}

              {isDecide && decidePanel === 'trades' && (
                <TradesPanel
                  generated={tradesGenerated}
                  loading={tradesLoading}
                  onGenerate={handleGenerateTrades}
                />
              )}
            </>
          )}

          {data?.usingProjections && (
            <p className="ff-proj-footnote">
              Pre-kickoff pts use ESPN projections.
            </p>
          )}
        </div>
      </section>
    </section>
  );
};

export default Dashboard;
